import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with stale-chunk recovery. After a deploy, the user's
 * cached index.html may reference JS chunks that no longer exist, causing
 * "Failed to fetch dynamically imported module". When that happens we
 * force a one-time hard reload to pick up the fresh asset manifest.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy<T>(async () => {
    const KEY = "lovable:chunk-reloaded";
    try {
      return await factory();
    } catch (err) {
      const msg = String((err as Error)?.message || err);
      const isChunkErr =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module");
      if (isChunkErr && typeof window !== "undefined") {
        const already = sessionStorage.getItem(KEY);
        if (!already) {
          sessionStorage.setItem(KEY, "1");
          window.location.reload();
          // Return a never-resolving promise so Suspense holds until reload.
          return new Promise<never>(() => {});
        }
      }
      throw err;
    }
  });
}
